// CRUD + live subscription helpers for the leads table.
// Replaces src/lib/leads.js (Firebase/Firestore version).
//
// Column naming: Postgres uses snake_case, the app uses camelCase.
// toRow()   converts a JS lead object  → DB row  (before writes)
// fromRow() converts a DB row          → JS lead (after reads)
// All page/component code stays unchanged.

import { supabase } from './supabase';
import { logActivity } from './activity';

// ---------------------------------------------------------------------------
// Column mapping helpers
// ---------------------------------------------------------------------------
function toRow(d) {
  return {
    legacy_id:                   d.legacyId != null ? String(d.legacyId) : null,
    company_name:                d.companyName   ?? '',
    industry:                    d.industry      ?? '',
    contact_name:                d.contactName   ?? '',
    contact_title:               d.contactTitle  ?? '',
    phone:                       d.phone         ?? '',
    email:                       d.email         ?? '',
    stage:                       d.stage         ?? 'Prospect',
    product_interest:            d.productInterest ?? 'Other',
    lead_source:                 d.leadSource    ?? 'Website',
    date_added:                  d.dateAdded
                                   ? toDateString(d.dateAdded)
                                   : new Date().toISOString().slice(0, 10),
    next_action:                 d.nextAction    ?? '',
    next_action_date:            d.nextActionDate ? toDateString(d.nextActionDate) : null,
    notes:                       d.notes         ?? '',
    estimated_monthly_value_kes: Number(d.estimatedMonthlyValueKES ?? 0),
    owner_id:                    d.ownerId       ?? null,
    owner_name:                  d.ownerName     ?? ''
  };
}

function fromRow(r) {
  if (!r) return null;
  return {
    id:                        r.id,
    legacyId:                  r.legacy_id,
    companyName:               r.company_name,
    industry:                  r.industry,
    contactName:               r.contact_name,
    contactTitle:              r.contact_title,
    phone:                     r.phone,
    email:                     r.email,
    stage:                     r.stage,
    productInterest:           r.product_interest,
    leadSource:                r.lead_source,
    dateAdded:                 r.date_added,
    nextAction:                r.next_action,
    nextActionDate:            r.next_action_date,
    notes:                     r.notes,
    estimatedMonthlyValueKES:  Number(r.estimated_monthly_value_kes ?? 0),
    ownerId:                   r.owner_id,
    ownerName:                 r.owner_name,
    createdBy:                 r.created_by,
    updatedBy:                 r.updated_by,
    createdAt:                 r.created_at,
    updatedAt:                 r.updated_at,
    lastActivityAt:            r.last_activity_at
  };
}

function toDateString(v) {
  if (!v) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  if (v instanceof Date)     return v.toISOString().slice(0, 10);
  if (v.toDate)              return v.toDate().toISOString().slice(0, 10);
  return null;
}

// ---------------------------------------------------------------------------
// Live subscription — replaces subscribeLeads(cb, onError)
// Returns an unsubscribe function.
// ---------------------------------------------------------------------------
export function subscribeLeads(cb, onError) {
  let channel;

  async function fetchAll() {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('last_activity_at', { ascending: false });

    if (error) {
      console.warn('[subscribeLeads] fetch error:', error.message);
      if (onError) onError(error);
      return;
    }
    cb((data ?? []).map(fromRow));
  }

  fetchAll();

  // Re-fetch the full list on any INSERT / UPDATE / DELETE so the kanban
  // board always shows the correct sorted order.
  channel = supabase
    .channel('leads-changes')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'leads' },
      () => fetchAll()
    )
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR' && onError) {
        onError(new Error('Realtime subscription failed — check your Supabase project settings.'));
      }
    });

  return () => supabase.removeChannel(channel);
}

// ---------------------------------------------------------------------------
// getLead — single lead by id
// ---------------------------------------------------------------------------
export async function getLead(id) {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return fromRow(data);
}

// ---------------------------------------------------------------------------
// createLead
// ---------------------------------------------------------------------------
export async function createLead(leadData) {
  const { data: { user } } = await supabase.auth.getUser();

  const row = {
    ...toRow(leadData),
    created_by:       user?.id ?? null,
    updated_by:       user?.id ?? null,
    owner_id:         leadData.ownerId   ?? user?.id   ?? null,
    owner_name:       leadData.ownerName ?? user?.user_metadata?.display_name ?? user?.email ?? '',
    last_activity_at: new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('leads')
    .insert(row)
    .select()
    .single();

  if (error) throw error;

  await logActivity(data.id, 'created', { after: leadData });
  return data.id;
}

// ---------------------------------------------------------------------------
// updateLead — only sends changed fields, keeps audit log small
// ---------------------------------------------------------------------------
export async function updateLead(id, patch) {
  const { data: { user } } = await supabase.auth.getUser();

  const before = await getLead(id);

  // Compute changed fields only.
  const changed = {};
  for (const [k, v] of Object.entries(patch)) {
    if (before?.[k] !== v) changed[k] = v;
  }
  if (Object.keys(changed).length === 0) return;

  const row = {
    ...toRow({ ...before, ...patch }),
    updated_by:       user?.id ?? null,
    last_activity_at: new Date().toISOString()
  };

  const { error } = await supabase
    .from('leads')
    .update(row)
    .eq('id', id);

  if (error) throw error;

  const action = changed.stage ? 'stage_changed' : 'updated';
  await logActivity(id, action, {
    before: pick(before, Object.keys(changed)),
    after:  changed
  });
}

// ---------------------------------------------------------------------------
// moveLeadStage — used by KanbanBoard drag-and-drop (unchanged interface)
// ---------------------------------------------------------------------------
export async function moveLeadStage(id, toStage) {
  return updateLead(id, { stage: toStage });
}

// ---------------------------------------------------------------------------
// deleteLead — activity rows cascade-delete via FK
// ---------------------------------------------------------------------------
export async function deleteLead(id) {
  const { error } = await supabase
    .from('leads')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ---------------------------------------------------------------------------
// findExistingLeadKeys — dedup check for import flows
// Returns { legacyIds: Set, emails: Set, error: Error|null }
// ---------------------------------------------------------------------------
export async function findExistingLeadKeys({ legacyIds = [], emails = [] } = {}) {
  const existingLegacy = new Set();
  const existingEmail  = new Set();
  let firstError = null;

  if (legacyIds.length > 0) {
    const clean = legacyIds.filter(Boolean).map(String);
    const { data, error } = await supabase
      .from('leads')
      .select('legacy_id')
      .in('legacy_id', clean);

    if (error) { firstError = error; }
    else { (data ?? []).forEach(r => r.legacy_id && existingLegacy.add(String(r.legacy_id))); }
  }

  if (emails.length > 0) {
    const clean = emails.filter(Boolean).map(e => String(e).toLowerCase());
    const { data, error } = await supabase
      .from('leads')
      .select('email')
      .in('email', clean);

    if (error && !firstError) { firstError = error; }
    else { (data ?? []).forEach(r => r.email && existingEmail.add(String(r.email).toLowerCase())); }
  }

  return { legacyIds: existingLegacy, emails: existingEmail, error: firstError };
}

// ---------------------------------------------------------------------------
// probeLeadsRead — health check (replaces Firebase version used by RulesHealthBanner)
// Returns { ok: boolean, code: string|null }
// ---------------------------------------------------------------------------
export async function probeLeadsRead() {
  const { error } = await supabase
    .from('leads')
    .select('id', { count: 'exact', head: true });

  if (error) {
    return { ok: false, code: error.code || 'unknown', message: error.message };
  }
  return { ok: true, code: null };
}

// ---------------------------------------------------------------------------
// bulkCreateLeads — used by Excel and Apollo importers (same interface)
// ---------------------------------------------------------------------------
export async function bulkCreateLeads(rows, opts = {}) {
  const { data: { user } } = await supabase.auth.getUser();

  let toCreate     = rows;
  const skipped    = [];
  let dedupSkipped = false;
  let dedupError   = null;
  let writeError   = null;

  if (opts.skipDuplicates) {
    const existing = await findExistingLeadKeys({
      legacyIds: rows.map(r => r.legacyId).filter(Boolean),
      emails:    rows.map(r => r.email).filter(Boolean)
    });

    if (existing.error) {
      console.warn('[bulkCreateLeads] dedup failed:', existing.error.message);
      dedupSkipped = true;
      dedupError   = existing.error;
      toCreate     = rows;
    } else {
      toCreate = [];
      for (const r of rows) {
        const dupByLegacy = r.legacyId && existing.legacyIds.has(String(r.legacyId));
        const dupByEmail  = r.email    && existing.emails.has(String(r.email).toLowerCase());
        if (dupByLegacy || dupByEmail) {
          skipped.push({
            legacyId:    r.legacyId    || null,
            email:       r.email       || null,
            companyName: r.companyName || '',
            reason:      dupByLegacy ? 'legacyId' : 'email'
          });
        } else {
          toCreate.push(r);
        }
      }
    }
  }

  const now  = new Date().toISOString();
  const ids  = [];
  const BATCH = 400;

  for (let i = 0; i < toCreate.length; i += BATCH) {
    const chunk = toCreate.slice(i, i + BATCH).map(r => ({
      ...toRow(r),
      created_by:       user?.id ?? null,
      updated_by:       user?.id ?? null,
      owner_id:         r.ownerId   ?? user?.id   ?? null,
      owner_name:       r.ownerName ?? user?.user_metadata?.display_name ?? user?.email ?? '',
      last_activity_at: now
    }));

    const { data, error } = await supabase
      .from('leads')
      .insert(chunk)
      .select('id');

    if (error) {
      console.warn('[bulkCreateLeads] batch failed:', error.message);
      writeError = error;
      break;
    }
    ids.push(...(data ?? []).map(r => r.id));
  }

  // Log imported events (best-effort).
  for (const id of ids) {
    try { await logActivity(id, 'imported'); } catch (_) { /* swallow */ }
  }

  return { created: ids, skipped, dedupSkipped, dedupError, writeError };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
function pick(obj, keys) {
  const out = {};
  for (const k of keys) if (obj && k in obj) out[k] = obj[k];
  return out;
}
